import { inject, Injectable } from '@angular/core';
import { SelectItem, SelectOption, SelectOptionSource } from '@siemens/element-ng/select';
import { SCDPerson } from '@tran-monorepo/interfaces';
import { ApplicationUser } from '@tran-monorepo/interfaces/applications';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { mapScdPersonToApplicationUser } from '../utils';
import { ScdService } from './scd.service';

interface CacheEntry {
    result$: Observable<SelectOption<ApplicationUser>[]>;
    expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class PersonOptionSourceService implements SelectOptionSource<ApplicationUser> {
    private static readonly CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

    private readonly scdService = inject(ScdService);
    private readonly searchQuery$ = new Subject<string>();
    private readonly searchResults$ = new ReplaySubject<SelectOption<ApplicationUser>[]>(1);
    private readonly valuesCache = new Map<string, CacheEntry>();

    constructor() {
        this.searchQuery$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(query => {
                    if (!query || !query.trim().length) {
                        return of([]);
                    }
                    return this.scdService.findAll(query).pipe(map(persons => this.personsToOptions(persons)));
                }),
                tap(results => this.searchResults$.next(results))
            )
            .subscribe();
    }

    public getOptionsForValues(values: ApplicationUser[]): Observable<SelectOption<ApplicationUser>[]> {
        const validKeys = values.map(user => user?.gid).filter(gid => gid && gid !== 'null' && gid.trim().length > 0);

        if (!validKeys.length) {
            return of([]);
        }

        const cacheKey = [...validKeys].sort().join(',');
        const cached = this.valuesCache.get(cacheKey);
        if (cached && Date.now() < cached.expiresAt) {
            return cached.result$;
        }

        const result$ = forkJoin(validKeys.map(gid => this.scdService.findOne(gid))).pipe(
            map(persons => this.personsToOptions(persons)),
            shareReplay(1)
        );
        this.valuesCache.set(cacheKey, { result$, expiresAt: Date.now() + PersonOptionSourceService.CACHE_TTL_MS });
        return result$;
    }

    public getOptionsForSearch(query: string): Observable<SelectItem<ApplicationUser>[]> {
        this.searchQuery$.next(query);
        return this.searchResults$.asObservable();
    }

    private personsToOptions(persons: SCDPerson[]): SelectOption<ApplicationUser>[] {
        return persons.map(person => ({
            value: mapScdPersonToApplicationUser(person),
            label: `${person.firstName} ${person.lastName} - ${person.department}`,
            type: 'option'
        }));
    }
}
